import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSetInLibrary } from './use-set-in-library';
import { useToaster } from '~/modules/root/context/ToasterContext';

const UNMOUNT_DELAY_MS = 500;

type ItemType = 'project' | 'product';

/**
 * Encapsulates the shared "Remove from Library" UX used by the projects and
 * products directory routes:
 *
 * - Wires `useSetInLibrary` with toast + undo handling.
 * - Tracks two-phase optimistic state — `removedFromLibraryIds` drives the
 *   fade-out animation, `unmountedFromLibraryIds` drives the grid reflow
 *   once the animation completes.
 * - Cleans up pending unmount timers on hook teardown.
 *
 * Returns:
 * - `libraryActionValue` to pass to `<LibraryActionProvider>` (and for routes
 *   that filter their tile arrays by `unmountedFromLibraryIds`).
 * - `handleRemoveFromLibrary` for callers that need to invoke removal directly
 *   (e.g. project route's prop-based threading via `renderProjectTileGrid`).
 * - `setInLibrary` so callers can guard on admin permissions (`undefined` for
 *   non-admins).
 */
interface LibraryRemovalUiOptions {
    /** Called once the server confirms a removal — not on request, so a failed
        call never fires it. */
    onAfterRemove?: (itemType: ItemType, itemId: number) => void;
}

export const useLibraryRemovalUi = (options?: LibraryRemovalUiOptions) => {
    const { toast } = useToaster();

    const onAfterRemoveRef = useRef(options?.onAfterRemove);
    onAfterRemoveRef.current = options?.onAfterRemove;

    const [removedFromLibraryIds, setRemovedFromLibraryIds] = useState<
        Set<number>
    >(new Set());
    const [unmountedFromLibraryIds, setUnmountedFromLibraryIds] = useState<
        Set<number>
    >(new Set());
    const unmountTimersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(
        new Map(),
    );
    const pendingRemovalNamesRef = useRef<Map<number, string>>(new Map());
    const setInLibraryRef = useRef<
        ((type: ItemType, id: number, val: boolean) => void) | null
    >(null);

    useEffect(() => {
        const timers = unmountTimersRef.current;
        return () => {
            timers.forEach((timer) => clearTimeout(timer));
            timers.clear();
        };
    }, []);

    const { setInLibrary } = useSetInLibrary(
        (error) => {
            if (error.Message) {
                toast(error.Message, { type: 'error' });
            }
        },
        (itemId, itemType, inLibrary) => {
            const name = pendingRemovalNamesRef.current.get(itemId) ?? '';
            pendingRemovalNamesRef.current.delete(itemId);
            const label = name ? `${name} - #${itemId}` : `#${itemId}`;
            if (!inLibrary) {
                const onUndo = () => {
                    pendingRemovalNamesRef.current.set(itemId, name);
                    setInLibraryRef.current?.(itemType, itemId, true);
                };
                toast(
                    <>
                        Successfully removed {label} from library.{' '}
                        <span
                            role="button"
                            tabIndex={0}
                            onClick={onUndo}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onUndo();
                                }
                            }}
                            className="hover:text-neutral-500 cursor-pointer underline transition-colors"
                        >
                            Click here
                        </span>{' '}
                        to restore.
                    </>,
                    { type: 'success' },
                );
                setRemovedFromLibraryIds((prev) => {
                    const next = new Set(prev);
                    next.add(itemId);
                    return next;
                });
                onAfterRemoveRef.current?.(itemType, itemId);
                const existing = unmountTimersRef.current.get(itemId);
                if (existing) clearTimeout(existing);
                const timer = setTimeout(() => {
                    setUnmountedFromLibraryIds((prev) => {
                        const next = new Set(prev);
                        next.add(itemId);
                        return next;
                    });
                    unmountTimersRef.current.delete(itemId);
                }, UNMOUNT_DELAY_MS);
                unmountTimersRef.current.set(itemId, timer);
            } else {
                toast(`Restored ${label} to library.`, { type: 'success' });
                const pending = unmountTimersRef.current.get(itemId);
                if (pending) {
                    clearTimeout(pending);
                    unmountTimersRef.current.delete(itemId);
                }
                setRemovedFromLibraryIds((prev) => {
                    if (!prev.has(itemId)) return prev;
                    const next = new Set(prev);
                    next.delete(itemId);
                    return next;
                });
                setUnmountedFromLibraryIds((prev) => {
                    if (!prev.has(itemId)) return prev;
                    const next = new Set(prev);
                    next.delete(itemId);
                    return next;
                });
            }
        },
    );
    setInLibraryRef.current = setInLibrary ?? null;

    // Stable callback identity — go through the ref so fetcher state
    // transitions don't change this function's reference. Without this, every
    // idle ↔ submitting transition would invalidate the context value below
    // and re-render every product tile on the page.
    const handleRemoveFromLibrary = useCallback(
        (itemType: ItemType, itemId: number, itemName: string) => {
            pendingRemovalNamesRef.current.set(itemId, itemName);
            setInLibraryRef.current?.(itemType, itemId, false);
        },
        [],
    );

    // Gate the context value on a boolean rather than the function identity —
    // admin status doesn't change mid-session, so this flips at most once.
    const canRemoveFromLibrary = !!setInLibrary;
    const libraryActionValue = useMemo(
        () => ({
            removeFromLibrary: canRemoveFromLibrary
                ? handleRemoveFromLibrary
                : undefined,
            removedFromLibraryIds,
            unmountedFromLibraryIds,
        }),
        [
            canRemoveFromLibrary,
            handleRemoveFromLibrary,
            removedFromLibraryIds,
            unmountedFromLibraryIds,
        ],
    );

    return {
        setInLibrary,
        handleRemoveFromLibrary,
        libraryActionValue,
        removedFromLibraryIds,
        unmountedFromLibraryIds,
    };
};
